import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { type MenuOption } from '@opallabs/concepts'
import {
  Button,
  InputLabel,
  SingleSelect,
  Tabs,
  TextInput,
} from '@opallabs/lapidary'
import { type CalendarItemKind } from '../../types'
import { type CreateCalendarItemParams } from '../useCreateCalendarItem'
import * as styles from './styles'

/** A channel a person can publish to, and the accounts it can publish from. */
export type CreateChannelOption = Readonly<{
  /** The numeric id the content endpoint takes. */
  id: string
  /** The channel's mark, drawn beside its name in the select and on the trigger. */
  icon?: ReactNode
  name: string
  accounts: ReadonlyArray<{ id: string; name: string }>
}>

export type CreateStampOption = Readonly<{ name: string; uuid: string }>

export type CreateFormProps = Readonly<{
  channels: readonly CreateChannelOption[]
  /** Post stamps the chosen channel can build content from. */
  contentStamps: readonly CreateStampOption[]
  date: Date
  /** Reads the day back to the person, so they can see where this will land. */
  dateLabel: string
  isSubmitting: boolean
  /** Moment stamps the workspace offers. */
  momentStamps: readonly CreateStampOption[]
  onCancel: () => void
  /** Says which channel the post stamps should be fetched for. */
  onChannelChange?: (channelId: string | null) => void
  onSubmit: (params: CreateCalendarItemParams) => void
  /** Lets whoever opened the form put the cursor in the title. */
  titleInputRef?: React.Ref<HTMLInputElement>
}>

/**
 * The longest title the moment and content endpoints accept, and the limit the
 * week view's own quick create holds people to.
 */
const MAX_TITLE_LENGTH = 100

const NO_STAMP = 'none'
const NO_ACCOUNT = 'none'

const toStampOptions = (
  options: readonly CreateStampOption[]
): Array<MenuOption<string>> => [
  { label: 'No stamp', value: NO_STAMP },
  ...options.map((each) => ({ label: each.name, value: each.uuid })),
]

export const CreateForm: React.FC<CreateFormProps> = ({
  channels,
  contentStamps,
  date,
  dateLabel,
  isSubmitting,
  momentStamps,
  onCancel,
  onChannelChange,
  onSubmit,
  titleInputRef,
}) => {
  const [kind, setKind] = useState<CalendarItemKind>('content')
  const [title, setTitle] = useState('')
  const [channelId, setChannelId] = useState<string | null>(
    channels.length === 1 ? channels[0].id : null
  )
  const [accountId, setAccountId] = useState<string>(NO_ACCOUNT)
  const [momentStampId, setMomentStampId] = useState<string>(NO_STAMP)
  const [contentStampId, setContentStampId] = useState<string>(NO_STAMP)

  // A channel is already chosen when it is the only one, so say so without
  // waiting for the person to touch the select.
  useEffect(() => {
    onChannelChange?.(channelId)
  }, [channelId, onChannelChange])

  const channelOptions = useMemo<Array<MenuOption<string>>>(
    () =>
      channels.map((each) => ({
        label: each.name,
        leadingNode: each.icon,
        value: each.id,
      })),
    [channels]
  )

  // An account belongs to one channel, so the list only makes sense once a
  // channel has been chosen.
  const accountOptions = useMemo<Array<MenuOption<string>>>(() => {
    const accounts =
      channels.find((each) => each.id === channelId)?.accounts ?? []
    return [
      { label: 'No account', value: NO_ACCOUNT },
      ...accounts.map((each) => ({ label: each.name, value: each.id })),
    ]
  }, [channelId, channels])

  const momentStampOptions = useMemo(
    () => toStampOptions(momentStamps),
    [momentStamps]
  )
  const contentStampOptions = useMemo(
    () => toStampOptions(contentStamps),
    [contentStamps]
  )

  const isTitleTooLong = title.length > MAX_TITLE_LENGTH
  const canSubmit =
    title.trim().length > 0 &&
    !isTitleTooLong &&
    !isSubmitting &&
    (kind === 'moment' || channelId !== null)

  const submit = () => {
    if (!canSubmit) return

    onSubmit(
      kind === 'moment'
        ? {
            date,
            kind: 'moment',
            stampId: momentStampId === NO_STAMP ? undefined : momentStampId,
            title: title.trim(),
          }
        : {
            accountId: accountId === NO_ACCOUNT ? undefined : accountId,
            date,
            kind: 'content',
            // Guarded by `canSubmit`, which will not let content through
            // without a channel.
            serviceId: channelId ?? '',
            stampId: contentStampId === NO_STAMP ? undefined : contentStampId,
            title: title.trim(),
          }
    )
  }

  return (
    <form
      className={styles.selectors.root}
      css={styles.root}
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <div className={styles.selectors.header}>
        <span className={styles.selectors.day}>{dateLabel}</span>

        {/* The kind is a tab set, as the design draws it. Only the switch is
            drawn: what it switches between is the rest of the form. */}
        <Tabs.Root
          value={kind}
          onValueChange={(value) => setKind(value as CalendarItemKind)}
        >
          <Tabs.List aria-label="What to make">
            <Tabs.Trigger value="content">Content</Tabs.Trigger>
            <Tabs.Trigger value="moment">Moment</Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>
      </div>

      <div className={styles.selectors.fields}>
        <div className={styles.selectors.field}>
          <InputLabel inputId="calendar-create-title" required text="Title" />
          <TextInput
            explicitError={
              isTitleTooLong
                ? `Title cannot be longer than ${MAX_TITLE_LENGTH} characters.`
                : ''
            }
            ref={titleInputRef}
            htmlId="calendar-create-title"
            placeholder="Add Name..."
            value={title}
            onChange={setTitle}
          />
        </div>

        {kind === 'content' ? (
          <>
            <div className={styles.selectors.field}>
              <SingleSelect.Root
                initialSelection={channelOptions.find(
                  (option) => option.value === channelId
                )}
                options={channelOptions}
                onChange={(option) => {
                  setChannelId(option?.value ?? null)
                  setAccountId(NO_ACCOUNT)
                  // The stamps on offer belong to the channel that was there
                  // before, so none of them apply now.
                  setContentStampId(NO_STAMP)
                }}
              >
                <SingleSelect.Body>
                  <SingleSelect.Label label="Channel" />
                  <SingleSelect.Trigger ariaLabel="Channel">
                    <SingleSelect.TriggerText />
                  </SingleSelect.Trigger>
                  <SingleSelect.Menu portalled={true}>
                    <SingleSelect.MenuItems />
                  </SingleSelect.Menu>
                </SingleSelect.Body>
              </SingleSelect.Root>
            </div>

            <div className={styles.selectors.field}>
              <SingleSelect.Root
                key={channelId ?? 'no-channel'}
                initialSelection={
                  accountOptions.find((option) => option.value === accountId) ??
                  accountOptions[0]
                }
                options={accountOptions}
                onChange={(option) => setAccountId(option?.value ?? NO_ACCOUNT)}
              >
                <SingleSelect.Body>
                  <SingleSelect.Label label="Account" />
                  {/* An account belongs to a channel, so there is nothing to
                    offer until one has been chosen. */}
                  <SingleSelect.Trigger
                    ariaLabel="Account"
                    disabled={channelId === null}
                  >
                    <SingleSelect.TriggerText />
                  </SingleSelect.Trigger>
                  <SingleSelect.Menu portalled={true}>
                    <SingleSelect.MenuItems />
                  </SingleSelect.Menu>
                </SingleSelect.Body>
              </SingleSelect.Root>
            </div>

            {contentStamps.length > 0 && (
              <div className={styles.selectors.field}>
                <SingleSelect.Root
                  key={channelId ?? 'no-channel'}
                  initialSelection={
                    contentStampOptions.find(
                      (option) => option.value === contentStampId
                    ) ?? contentStampOptions[0]
                  }
                  options={contentStampOptions}
                  onChange={(option) =>
                    setContentStampId(option?.value ?? NO_STAMP)
                  }
                >
                  <SingleSelect.Body>
                    <SingleSelect.Label label="Apply stamp" />
                    <SingleSelect.Trigger ariaLabel="Apply stamp">
                      <SingleSelect.TriggerText />
                    </SingleSelect.Trigger>
                    <SingleSelect.Menu portalled={true}>
                      <SingleSelect.MenuItems />
                    </SingleSelect.Menu>
                  </SingleSelect.Body>
                </SingleSelect.Root>
              </div>
            )}
          </>
        ) : (
          momentStamps.length > 0 && (
            <div className={styles.selectors.field}>
              <SingleSelect.Root
                initialSelection={
                  momentStampOptions.find(
                    (option) => option.value === momentStampId
                  ) ?? momentStampOptions[0]
                }
                options={momentStampOptions}
                onChange={(option) =>
                  setMomentStampId(option?.value ?? NO_STAMP)
                }
              >
                <SingleSelect.Body>
                  <SingleSelect.Label label="Apply stamp" />
                  <SingleSelect.Trigger ariaLabel="Apply stamp">
                    <SingleSelect.TriggerText />
                  </SingleSelect.Trigger>
                  <SingleSelect.Menu portalled={true}>
                    <SingleSelect.MenuItems />
                  </SingleSelect.Menu>
                </SingleSelect.Body>
              </SingleSelect.Root>
            </div>
          )
        )}
      </div>

      <div className={styles.selectors.actions}>
        <Button
          buttonText="Cancel"
          name="calendar-create-cancel"
          type="button"
          variant="tertiary"
          onClick={onCancel}
        />
        <Button
          buttonText="Create"
          disabled={!canSubmit}
          name="calendar-create-submit"
          type="submit"
          variant="primary"
        />
      </div>
    </form>
  )
}
CreateForm.displayName = 'Calendar: CreateForm'
